import { Fragment, useEffect, useRef, useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import {
    ArrowLeft,
    CreditCard,
    GitBranch,
    History,
    Pencil,
    QrCode,
    ShoppingCart,
    Users,
} from 'lucide-react'

import type {
    PackagingDraftItem,
    SalesOrderItem,
} from '@/lib/api/sales-order-api'
import type { OrdersSearch } from './index'

import type { OrderActionItem } from '@/components/sections/order-actions'
import type { OrderActionGroup } from '@/components/orders/order-action-menu'
import { PageBackButton } from '@/components/common/PageBackButton'
import { captureOrderFollowup } from '@/lib/order-followup-tracking'
import { OrderLotTraceDialog } from '@/components/orders/order-lot-trace-dialog'
import { FormSection } from '@/components/forms/form-section'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { PaymentQRDialog } from '@/components/payment-qr-dialog'
import { OrderChannelBadge } from '@/components/sections/order-channel-badge'
import { OrderStatusBadge } from '@/components/sections/order-status-badge'
import { PaymentStatusBadge } from '@/components/sections/payment-status-badge'
import { OrderSLAPanel } from '@/components/sections/order-sla-panel'
import { OrderTimeline } from '@/components/sections/order-timeline'
import { useOrderWorkflowActions } from '@/components/sections/order-actions'
import { OrderActionMenu } from '@/components/orders/order-action-menu'
import { OrderPaymentHistory } from '@/components/orders/order-payment-history'
import { OrderPXKCard } from '@/components/sections/order-pxk-card'
import { mergeOrderLines } from '@/lib/order-line-order'
import { OrderPackagingDeducted } from '@/components/orders/order-packaging-deducted'
import { SalesReturnHistory } from '@/components/orders/sales-return-history'
import { AwaitingPackagingPanel } from '@/components/orders/awaiting-packaging-panel'
import { OrderMaterialDetailRow } from '@/components/orders/order-material-detail-row'
import { EInvoicePanel } from '@/components/sections/einvoice-panel'
import { CarrierTrackingPanel } from '@/components/carrier/carrier-tracking-panel'
import { isCarrierShipmentRetryable } from '@/lib/carrier-status'
import { useInvoiceDocumentActions } from '@/components/sections/invoice-download-actions'
import { OrderDeliveryCameraCard } from '@/components/orders/order-delivery-camera-card'
import { OrderGiftCard } from '@/components/orders/order-gift-card'
import { PaymentForm } from '@/components/forms/payment-form'
import { EditOrderForm } from '@/components/forms/edit-order-form'
import { useAuth } from '@/hooks/use-auth'
import { useOrderPaymentTracking } from '@/hooks/use-order-payment-tracking'
import {
    useOrderPackagingDraft,
    useOrderPayments,
    useOrderTimeline,
    useRecordOrderPayments,
    useSalesOrder,
} from '@/hooks/use-sales-order-query'
import { useProductLotOutNotesQuery } from '@/hooks/use-product-lot-query'
import { useInvoiceSettings } from '@/hooks/use-invoice-query'
import { useCarrierShipment } from '@/hooks/use-carrier-shipment-query'
import { useFeatureFlag } from '@/hooks/use-feature-flag'
import { useIsMobile } from '@/hooks/use-is-mobile'
import { formatVnd } from '@/lib/format'
import { orderRemaining } from '@/lib/order-money'
import { ordererLine } from '@/lib/order-orderer'
import { LIST_SEARCH_KEYS, recallListSearch } from '@/lib/list-search-store'
import { canEditOrder, canEditOrderItems } from '@/lib/order-edit'
import { ApiError } from '@/lib/api'
import { InternalOrderCodeRow } from '@/components/orders/internal-order-code-row'
import { KiotVietItemCell } from '@/components/orders/kiotviet-item-cell'
import { KiotVietLinkDialog } from '@/components/orders/kiotviet-link-dialog'
import { KiotVietUnlinkedNotice } from '@/components/orders/kiotviet-unlinked-notice'
import { countKiotVietUnlinked, isKiotVietLinkable } from '@/lib/kiotviet-link'
import { orderHandoverAt } from '@/lib/einvoice-handover'
import { m } from '@/lib/paraglide/messages'

interface OrderDetailSearch {
    /** Giữ mode "Chỉnh sửa" trên URL để reload không rớt mode + back từ mode sửa
     *  quay về chính màn chi tiết (thay vì nhảy về list). */
    edit?: boolean
    /** Set when the order is opened from the pending-invoice list. */
    from?: 'pending_list'
}

export const Route = createFileRoute('/_app/_authed/_sidebar/orders/$orderId')({
    validateSearch: (search: Record<string, unknown>): OrderDetailSearch => ({
        edit: search.edit === true || search.edit === 'true' ? true : undefined,
        from: search.from === 'pending_list' ? 'pending_list' : undefined,
    }),
    component: OrderDetailPage,
})

export function OrderDetailPage() {
    const { orderId } = Route.useParams()
    const { edit, from } = Route.useSearch()
    const navigate = useNavigate({ from: '/orders/$orderId' })
    // Đẩy mode vào URL: bật = push (back sẽ về chi tiết), tắt = replace (không
    // để lại URL ?edit=true thừa trong history).
    const startEditing = () =>
        navigate({ search: (prev) => ({ ...prev, edit: true }) })
    const stopEditing = () =>
        navigate({
            search: (prev) => ({ ...prev, edit: undefined }),
            replace: true,
        })
    const [payOpen, setPayOpen] = useState(false)
    const [qrOpen, setQrOpen] = useState(false)
    const [traceOpen, setTraceOpen] = useState(false)
    // Dòng KiotViet đang mở popup "Nối sản phẩm" (M-145).
    const [linkingItem, setLinkingItem] = useState<SalesOrderItem | null>(null)
    const { hasPermission, user, impersonating } = useAuth()
    // Nối SP đổi mapping SP ↔ mã KiotViet → server đòi products.update.
    const canLinkKiotViet = hasPermission('products.update')

    const { data: order, error, isLoading } = useSalesOrder(orderId)
    const hasLoadedOrder = Boolean(order) && !error
    const dependencyOrderId = hasLoadedOrder ? orderId : ''
    const { data: linkedPxkList } = useProductLotOutNotesQuery(
        { sales_order_id: orderId, per_page: 1 },
        { enabled: hasLoadedOrder },
    )
    const linkedPxk = linkedPxkList?.data?.[0] ?? null
    const { data: timeline = [], isLoading: timelineLoading } =
        useOrderTimeline(dependencyOrderId)
    const { data: payments = [] } = useOrderPayments(dependencyOrderId)
    const { data: invoiceSettings } = useInvoiceSettings()
    // A parcel booked with a shipping carrier leaves our hands at dispatch: the
    // driver controls no longer apply to it, and its progress comes from the
    // carrier's pushes instead.
    const carrierEnabled = Boolean(useFeatureFlag('dev-carrier-shipping'))
    const carrierShipmentQuery = useCarrierShipment(orderId, hasLoadedOrder)
    const carrierShipment = carrierShipmentQuery.data
    const carrierShipmentState = carrierShipmentQuery.isLoading
        ? 'loading'
        : carrierShipmentQuery.isError
          ? 'error'
          : carrierShipment && !isCarrierShipmentRetryable(carrierShipment)
            ? 'active'
            : carrierEnabled
              ? 'none'
              : 'disabled'
    const { data: packagingDraft = [] } = useOrderPackagingDraft(
        orderId,
        hasLoadedOrder,
    )
    const recordPayments = useRecordOrderPayments(orderId)
    const paymentTracking = useOrderPaymentTracking({ open: payOpen, order })

    // Mobile gom mọi hành động phụ vào một menu kebab ở header. Hook được gọi
    // ở đây (trước mọi early return) để thứ tự hook không đổi giữa các lần
    // render, và để 3 nhóm nút cùng chảy vào một menu duy nhất.
    const isMobile = useIsMobile()
    const workflowActions = useOrderWorkflowActions({
        orderId,
        status: order?.status ?? 'new',
        carrierShipmentState,
    })
    const documentActions = useInvoiceDocumentActions({
        orderId,
        order: order ?? undefined,
        companyName: invoiceSettings?.company_name,
        companyPhone: invoiceSettings?.company_phone,
    })

    // `?edit=true` chỉ thực sự vào mode sửa khi đơn được phép sửa VÀ user có
    // quyền — gate y hệt nút "Chỉnh sửa" để URL không mở form sửa cho đơn/không
    // được phép (backend vẫn chặn khi lưu, nhưng không để UI hiện sai).
    const editing =
        !!order &&
        edit === true &&
        canEditOrder(order.status, order.payment_status) &&
        hasPermission('sales_orders.update')
    const actorId = impersonating?.actor.id ?? user?.id
    const isImpersonating = !!impersonating
    const detailVisit = useRef('')
    const editVisit = useRef('')
    useEffect(() => {
        if (!hasLoadedOrder || isLoading || !actorId) return
        const actor = { actor: actorId, impersonating: isImpersonating }
        const visit = `${orderId}:${actorId}:${isImpersonating}`
        if (detailVisit.current !== visit) {
            detailVisit.current = visit
            captureOrderFollowup(orderId, 'detail_opened', undefined, actor)
        }
        if (editing && editVisit.current !== visit) {
            editVisit.current = visit
            captureOrderFollowup(orderId, 'edit_opened', undefined, actor)
        }
        if (!editing) editVisit.current = ''
    }, [orderId, hasLoadedOrder, isLoading, editing, actorId, isImpersonating])

    if (isLoading) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.common_loading()}
            </div>
        )
    }
    if (error instanceof ApiError && error.status === 403) {
        return (
            <div className="p-4 text-sm font-medium text-destructive md:p-6">
                {error.requestId
                    ? m.order_detail_forbidden_with_request_id({
                          requestId: error.requestId,
                      })
                    : m.order_detail_forbidden()}
            </div>
        )
    }
    if (!order) {
        return (
            <div className="text-muted-foreground text-sm">
                {m.order_detail_not_found()}
            </div>
        )
    }

    const items = order.items ?? []
    // Vật tư thuộc định mức của DÒNG SP (order_item_id set) — render thành sub-row
    // dưới đúng sản phẩm, đồng nhất với màn Tạo/Sửa. Vật tư thêm tay/order-level
    // (order_item_id null, KHÔNG thuộc định mức SP) KHÔNG hiện trong bảng SP.
    // Nguồn = draft đã lưu (KHÔNG recompute BOM) để đơn cũ không sai khi sau này
    // đổi công thức/giá vật tư. Giá đã là snapshot (0 nếu gộp vào giá SP).
    const materialsByItem = new Map<string, PackagingDraftItem[]>()
    for (const d of packagingDraft) {
        if (d.quantity > 0 && d.order_item_id) {
            const list = materialsByItem.get(d.order_item_id) ?? []
            list.push(d)
            materialsByItem.set(d.order_item_id, list)
        }
    }
    // Vật tư KHÔNG gắn dòng đơn → dòng ngang hàng sản phẩm.
    //
    // Lọc theo order_item_id chứ KHÔNG theo source: hai bộ lọc phải phủ kín và
    // không chồng nhau. Lọc cũ dùng source='manual' để lại khoảng trống — dòng
    // bom_autofill mà order_item_id NULL (DB thật đang có 15 dòng) không vào
    // nhóm nào nên biến mất khỏi bảng, trong khi tổng đơn lấy từ server vẫn
    // tính tiền chúng. Nó cũng chồng lấn ở chiều ngược lại: dòng source=manual
    // có order_item_id sẽ hiện hai lần trong cùng một bảng.
    const manualMaterials = packagingDraft.filter(
        (d) => !d.order_item_id && d.quantity > 0,
    )
    // Thứ tự bảng hàng hóa bám sort_order đã lưu — sản phẩm và vật tư xen kẽ
    // đúng như người bán nhập trên form (FD #358). Đơn cũ (sort_order = 0) rơi
    // về thứ tự cũ: sản phẩm trước, vật tư sau.
    const displayRows = mergeOrderLines(
        items.map((it) => it.sort_order),
        manualMaterials.map((d) => d.sort_order),
    )
    // Round cả 2 để tránh legacy orders chưa migrate hiển thị âm 0.xx.
    // M-44: phải thu = tổng đơn − giá trị hàng khách đã trả sau giao + phí trả
    // hàng bên bán giữ lại.
    const returnedAmount = Math.round(order.returned_amount ?? 0)
    const returnFee = Math.round(order.return_fee_amount ?? 0)
    const remaining = Math.round(orderRemaining(order))
    const paymentDefault = remaining > 0 ? remaining : 0
    // Đơn đã hủy / đã hoàn: ẩn mọi nút thao tác thanh toán (QR, ghi nhận, đánh
    // dấu đã trả) vì không còn nghĩa vụ thu tiền.
    const noCollection =
        order.status === 'cancelled' || order.status === 'returned'
    // Derive VAT breakdown from total (entity stores only the post-VAT total).
    // Ưu tiên VAT snapshot của đơn (người bán chỉnh riêng); đơn cũ fallback global.
    // Legacy orders có total_amount decimal → Math.round để VAT hiển thị integer.
    const vatPercent = order.vat_percent ?? invoiceSettings?.vat_percent ?? 0
    const vatAmount =
        order.vat_amount != null
            ? Math.round(order.vat_amount)
            : vatPercent > 0
              ? Math.round(
                    order.total_amount -
                        Math.round(order.total_amount / (1 + vatPercent / 100)),
                )
              : 0
    // Phụ thu KiotViet (đã gộp trong total_amount). Trừ khỏi tiền hàng để dòng
    // "Tổng tiền hàng" không cộng nhầm phụ thu.
    const surcharge = order.kiotviet_surcharge
        ? Math.round(order.kiotviet_surcharge)
        : 0
    const subtotalPreVat =
        Math.round(order.total_amount) - vatAmount - surcharge
    // Chiết khấu toàn đơn (%): tính thẳng số tiền giảm = %CK × tổng dòng hàng
    // (item.subtotal đã gồm CK dòng, chưa gồm CK toàn đơn). Nhân trực tiếp thay
    // vì suy ngược từ subtotalPreVat để tránh chia cho 0 khi CK = 100%.
    const discountPercent = order.discount_percent ?? 0
    const discountAmount =
        discountPercent > 0
            ? Math.round(
                  (items.reduce((sum, it) => sum + it.subtotal, 0) *
                      discountPercent) /
                      100,
              )
            : 0

    const canShowEdit =
        canEditOrder(order.status, order.payment_status) &&
        hasPermission('sales_orders.update') &&
        !editing

    // Hành động thanh toán — cùng điều kiện với hàng nút trong card Thanh toán,
    // mô tả một lần rồi render ở card (desktop) hoặc menu kebab (mobile).
    const paymentActions: OrderActionItem[] =
        remaining > 0 && !noCollection
            ? [
                  // Xem QR chỉ cần quyền đọc; ghi nhận tiền cần customers.payment.
                  ...(hasPermission('sales_orders.read')
                      ? [
                            {
                                key: 'payment-qr',
                                label: m.payment_qr_open_button(),
                                icon: <QrCode className="size-4" />,
                                variant: 'outline' as const,
                                onSelect: () => setQrOpen(true),
                            },
                        ]
                      : []),
                  ...(hasPermission('customers.payment')
                      ? [
                            {
                                key: 'record-payment',
                                label: m.order_record_payment(),
                                onSelect: () => setPayOpen(true),
                            },
                            // Một chạm: ghi nhận toàn bộ phần còn lại là đã
                            // thu. Dành cho tiền mặt / chuyển khoản ngoài sổ /
                            // nội dung không khớp — nguồn sự thật thủ công,
                            // độc lập với webhook SePay.
                            // Không gửi reference: cột này unique và là khoá
                            // chống trùng giao dịch SePay, một chuỗi cố định
                            // làm mọi lần bấm sau lần đầu bị 409.
                            {
                                key: 'mark-paid',
                                label: m.order_mark_paid_button(),
                                disabled: recordPayments.isPending,
                                onSelect: () =>
                                    paymentTracking.markPaid((callbacks) =>
                                        recordPayments.mutate(
                                            [
                                                {
                                                    amount: remaining,
                                                    method: 'cash',
                                                },
                                            ],
                                            callbacks,
                                        ),
                                    ),
                            },
                        ]
                      : []),
              ]
            : []

    // Truy xuất nguồn gốc — chỉ khi đơn có PXK approved (có lô để trace).
    const traceAction: OrderActionItem | null =
        !editing && linkedPxk?.status === 'approved'
            ? {
                  key: 'lot-trace',
                  label: m.order_lot_trace_button(),
                  icon: <GitBranch className="size-4" />,
                  variant: 'outline',
                  onSelect: () => setTraceOpen(true),
              }
            : null

    // Mobile: hành động chính của trạng thái ở ngoài, phần còn lại vào kebab.
    const primaryAction = editing
        ? null
        : (workflowActions.items.find((item) => item.primary) ?? null)
    const menuGroups: OrderActionGroup[] = [
        {
            key: 'order',
            items: [
                ...(canShowEdit
                    ? [
                          {
                              key: 'edit',
                              label: m.order_edit_btn(),
                              icon: <Pencil className="size-4" />,
                              onSelect: startEditing,
                          },
                      ]
                    : []),
                ...(editing
                    ? []
                    : workflowActions.items.filter(
                          (item) => item.key !== primaryAction?.key,
                      )),
                ...(traceAction ? [traceAction] : []),
            ],
        },
        {
            key: 'payment',
            label: m.order_payment_title(),
            items: editing ? [] : paymentActions,
        },
        {
            key: 'documents',
            label: m.order_actions_group_documents(),
            items: documentActions.items,
        },
    ]

    const backButton = (
        <PageBackButton asChild>
            <Link
                to="/orders"
                search={
                    recallListSearch<OrdersSearch>(LIST_SEARCH_KEYS.orders) ??
                    {}
                }
            >
                <ArrowLeft className="size-4" />
            </Link>
        </PageBackButton>
    )

    const orderCreatedAtLabel = new Date(order.created_at).toLocaleString(
        'vi-VN',
    )

    return (
        <div className="space-y-4 page-pad md:space-y-6">
            {/* Header. Mobile xếp thành 4 hàng riêng (tiêu đề / trạng thái /
                ngày giờ / hành động) vì nhồi chung một hàng làm nút hành động
                đè lên mã đơn, còn badge và ngày giờ bị rớt dòng lẻ tẻ. */}
            {isMobile ? (
                <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        {backButton}
                        <h1 className="min-w-0 flex-1 truncate text-xl font-semibold font-mono">
                            {order.order_code}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <OrderStatusBadge status={order.status} />
                        <PaymentStatusBadge status={order.payment_status} />
                    </div>
                    <p className="text-sm text-muted-foreground whitespace-nowrap">
                        {orderCreatedAtLabel}
                    </p>
                    <div className="flex items-center justify-end gap-2">
                        {primaryAction && (
                            <Button
                                className="flex-1"
                                variant={primaryAction.variant}
                                disabled={primaryAction.disabled}
                                title={primaryAction.title}
                                onClick={primaryAction.onSelect}
                            >
                                {primaryAction.icon}
                                {primaryAction.label}
                            </Button>
                        )}
                        <OrderActionMenu
                            groups={menuGroups}
                            label={m.order_actions_menu_label()}
                        />
                    </div>
                </div>
            ) : (
                <div className="flex flex-wrap items-start gap-3">
                    {backButton}
                    <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                            <h1 className="text-xl font-semibold font-mono">
                                {order.order_code}
                            </h1>
                            <OrderStatusBadge status={order.status} />
                            <PaymentStatusBadge status={order.payment_status} />
                        </div>
                        <p className="text-sm text-muted-foreground mt-0.5">
                            {orderCreatedAtLabel}
                        </p>
                    </div>
                    {canShowEdit && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={startEditing}
                        >
                            <Pencil className="mr-1 size-4" />{' '}
                            {m.order_edit_btn()}
                        </Button>
                    )}
                    <div className="flex flex-wrap gap-2">
                        {documentActions.items.map((item) => (
                            <Button
                                key={item.key}
                                size="sm"
                                variant="outline"
                                disabled={item.disabled}
                                title={item.title}
                                onClick={item.onSelect}
                            >
                                {item.icon}
                                {item.label}
                            </Button>
                        ))}
                    </div>
                </div>
            )}

            {/* SLA info */}
            <OrderSLAPanel
                deadlineAt={order.sla_deadline_at}
                breached={order.sla_breached}
                warningAt={order.sla_warning_at}
            />

            {/* Workflow actions — mobile đã gom vào menu kebab ở header. */}
            {!editing && !isMobile && (
                <div className="flex flex-wrap items-center gap-2">
                    {workflowActions.items.map((item) => (
                        <Button
                            key={item.key}
                            size="sm"
                            variant={item.variant}
                            disabled={item.disabled}
                            aria-disabled={item.muted || undefined}
                            className={item.muted ? 'opacity-50' : undefined}
                            title={item.title}
                            onClick={item.onSelect}
                        >
                            {item.icon}
                            {item.label}
                        </Button>
                    ))}
                </div>
            )}
            {!editing && workflowActions.dialogs}
            {documentActions.dialogs}

            {/* Vận chuyển qua hãng — chỉ hiện khi đơn có vận đơn đang chạy. */}
            {/* A pending claim the carrier never acknowledged is retryable: the
                booking form takes over, so the tracking panel stays hidden. */}
            {!editing && carrierShipmentState === 'active' && (
                <CarrierTrackingPanel orderId={orderId} />
            )}

            {/* Đơn chờ đóng gói — panel kiểm tra tồn để cashier biết khi nào Confirm được. */}
            {!editing && order.status === 'awaiting_packaging' && (
                <AwaitingPackagingPanel order={order} items={items} />
            )}

            {/* PXK linked — hiển thị cho thủ kho click vào detail lấy hàng */}
            {!editing && <OrderPXKCard pxk={linkedPxk} />}

            {/* Truy xuất nguồn gốc — mobile đã gom vào menu kebab ở header. */}
            {traceAction && !isMobile && (
                <div className="flex justify-end">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={traceAction.onSelect}
                    >
                        <GitBranch className="mr-1 size-4" />
                        {traceAction.label}
                    </Button>
                </div>
            )}
            <OrderLotTraceDialog
                open={traceOpen}
                orderId={orderId}
                orderStatus={order?.status}
                onOpenChange={setTraceOpen}
            />
            <KiotVietLinkDialog
                orderId={orderId}
                item={linkingItem}
                open={linkingItem !== null}
                onOpenChange={(open) => {
                    if (!open) setLinkingItem(null)
                }}
            />

            {/* Edit form */}
            {editing && (
                <div className="space-y-4">
                    <div className="flex items-center gap-2">
                        <Pencil className="size-5 text-primary" />
                        <h2 className="text-lg font-semibold tracking-tight">
                            {m.order_edit_title()}
                        </h2>
                    </div>
                    <EditOrderForm
                        order={order}
                        itemsEditable={canEditOrderItems(order.status)}
                        hasActiveCarrierShipment={Boolean(
                            carrierShipment &&
                            carrierShipment.status !== 'cancelled',
                        )}
                        onSuccess={stopEditing}
                        onCancel={stopEditing}
                    />
                </div>
            )}

            {/* Main content grid */}
            {!editing && (
                <div className="grid gap-5 lg:grid-cols-[minmax(320px,380px)_minmax(0,1fr)] lg:items-start">
                    {/* Right column — products + payment (wide) */}
                    <div className="min-w-0 space-y-5 lg:col-start-2 lg:row-start-1">
                        {/* Items */}
                        <FormSection
                            icon={<ShoppingCart />}
                            title={m.order_products()}
                        >
                            <KiotVietUnlinkedNotice
                                count={countKiotVietUnlinked(items)}
                                canLink={canLinkKiotViet}
                            />
                            <div className="overflow-x-auto rounded-lg border">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>
                                                {m.order_products()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                SL
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.order_unit_price()}
                                            </TableHead>
                                            <TableHead className="text-right">
                                                CK
                                            </TableHead>
                                            <TableHead className="text-right">
                                                {m.order_subtotal()}
                                            </TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {displayRows.map((row) => {
                                            if (row.kind === 'material') {
                                                const d =
                                                    manualMaterials[row.index]
                                                return (
                                                    <OrderMaterialDetailRow
                                                        key={d.id}
                                                        d={d}
                                                        variant="standalone"
                                                    />
                                                )
                                            }
                                            const item = items[row.index]
                                            return (
                                                <Fragment key={item.id}>
                                                    <TableRow>
                                                        <TableCell>
                                                            <KiotVietItemCell
                                                                item={item}
                                                                canLink={
                                                                    canLinkKiotViet
                                                                }
                                                                linkable={isKiotVietLinkable(
                                                                    order.status,
                                                                )}
                                                                onLink={
                                                                    setLinkingItem
                                                                }
                                                            />
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {item.quantity}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {formatVnd(
                                                                item.unit_price,
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            {
                                                                item.discount_percent
                                                            }
                                                            %
                                                        </TableCell>
                                                        <TableCell className="text-right font-medium">
                                                            {formatVnd(
                                                                item.subtotal,
                                                            )}
                                                        </TableCell>
                                                    </TableRow>
                                                    {(
                                                        materialsByItem.get(
                                                            item.id,
                                                        ) ?? []
                                                    ).map((d) => (
                                                        <OrderMaterialDetailRow
                                                            key={d.id}
                                                            d={d}
                                                            variant="sub"
                                                        />
                                                    ))}
                                                </Fragment>
                                            )
                                        })}
                                        {discountPercent > 0 && (
                                            <TableRow className="bg-muted/30">
                                                <TableCell
                                                    colSpan={4}
                                                    className="text-right text-muted-foreground"
                                                >
                                                    {m.order_discount_label({
                                                        percent:
                                                            String(
                                                                discountPercent,
                                                            ),
                                                    })}
                                                </TableCell>
                                                <TableCell className="text-right text-muted-foreground">
                                                    −{formatVnd(discountAmount)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        {vatPercent > 0 && (
                                            <>
                                                <TableRow className="bg-muted/30">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right text-muted-foreground"
                                                    >
                                                        {m.order_items_subtotal()}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatVnd(
                                                            subtotalPreVat,
                                                        )}
                                                    </TableCell>
                                                </TableRow>
                                                <TableRow className="bg-muted/30">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right text-muted-foreground"
                                                    >
                                                        {m.order_vat_label({
                                                            percent:
                                                                String(
                                                                    vatPercent,
                                                                ),
                                                        })}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {formatVnd(vatAmount)}
                                                    </TableCell>
                                                </TableRow>
                                            </>
                                        )}
                                        {surcharge > 0 && (
                                            <TableRow className="bg-muted/30">
                                                <TableCell
                                                    colSpan={4}
                                                    className="text-right text-muted-foreground"
                                                >
                                                    {m.order_surcharge_label()}
                                                </TableCell>
                                                <TableCell className="text-right">
                                                    {formatVnd(surcharge)}
                                                </TableCell>
                                            </TableRow>
                                        )}
                                        <TableRow className="bg-muted/30 font-bold">
                                            <TableCell
                                                colSpan={4}
                                                className="text-right"
                                            >
                                                {m.order_grand_total()}
                                            </TableCell>
                                            <TableCell className="text-right">
                                                {formatVnd(
                                                    Math.round(
                                                        order.total_amount,
                                                    ),
                                                )}
                                            </TableCell>
                                        </TableRow>
                                        {(() => {
                                            const totalWeight = items.reduce(
                                                (sum, item) =>
                                                    sum +
                                                    (item.weight_value ?? 0) *
                                                        item.quantity,
                                                0,
                                            )
                                            if (totalWeight <= 0) return null
                                            const unit =
                                                items.find((i) => i.weight_unit)
                                                    ?.weight_unit ?? 'g'
                                            return (
                                                <TableRow className="text-muted-foreground text-xs">
                                                    <TableCell
                                                        colSpan={4}
                                                        className="text-right"
                                                    >
                                                        {m.order_total_weight()}
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        {totalWeight >= 1000
                                                            ? `${(totalWeight / 1000).toFixed(1)} kg`
                                                            : `${totalWeight} ${unit}`}
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })()}
                                    </TableBody>
                                </Table>
                            </div>
                        </FormSection>

                        {/* Payments */}
                        <FormSection
                            icon={<CreditCard />}
                            title={m.order_payment_title()}
                            action={
                                // Mobile: các nút này đã nằm trong menu kebab ở
                                // header — để lại đây thì header card không đủ
                                // chỗ và đẩy tràn ngang cả trang.
                                !isMobile && paymentActions.length > 0 ? (
                                    <div className="flex gap-2">
                                        {paymentActions.map((item) => (
                                            <Button
                                                key={item.key}
                                                size="sm"
                                                variant={item.variant}
                                                disabled={item.disabled}
                                                onClick={item.onSelect}
                                            >
                                                {item.icon}
                                                {item.label}
                                            </Button>
                                        ))}
                                    </div>
                                ) : null
                            }
                        >
                            <div className="space-y-2">
                                {returnedAmount > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            {m.order_returned_amount()}
                                        </span>
                                        <span className="font-medium">
                                            {formatVnd(returnedAmount)}
                                        </span>
                                    </div>
                                )}
                                {returnFee > 0 && (
                                    <div className="flex justify-between text-sm">
                                        <span className="text-muted-foreground">
                                            {m.sales_return_fee_kept_label()}
                                        </span>
                                        <span className="font-medium">
                                            {formatVnd(returnFee)}
                                        </span>
                                    </div>
                                )}
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {m.order_paid()}
                                    </span>
                                    <span className="font-medium">
                                        {formatVnd(
                                            Math.round(order.paid_amount),
                                        )}
                                    </span>
                                </div>
                                <div className="flex justify-between text-sm">
                                    <span className="text-muted-foreground">
                                        {remaining < 0
                                            ? m.order_overpaid()
                                            : m.order_remaining()}
                                    </span>
                                    <span
                                        className={
                                            remaining > 0
                                                ? 'font-bold text-destructive'
                                                : 'font-medium'
                                        }
                                    >
                                        {formatVnd(Math.abs(remaining))}
                                    </span>
                                </div>
                                {remaining > 0 &&
                                    order.settlement_intent ===
                                        'bank_transfer' && (
                                        <p className="text-xs font-medium text-amber-600">
                                            {m.order_settlement_intent_bank()}
                                        </p>
                                    )}
                                {remaining > 0 &&
                                    order.settlement_intent === 'credit' && (
                                        <p className="text-xs text-muted-foreground">
                                            {m.order_settlement_intent_credit()}
                                        </p>
                                    )}
                                <OrderPaymentHistory
                                    orderId={orderId}
                                    payments={payments}
                                    canChangeMethod={hasPermission(
                                        'customers.payment',
                                    )}
                                    isQuickSale={order.is_quick_sale}
                                />
                                <EInvoicePanel
                                    orderId={orderId}
                                    orderCode={order.order_code}
                                    orderStatus={order.status}
                                    isQuickSale={order.is_quick_sale}
                                    canExport={hasPermission('invoices.export')}
                                    customerId={order.customer_id}
                                    // Bán ngay: khách nhận hàng lúc bán (M-132)
                                    // — chưa xuất kho thì mốc giao là lúc tạo đơn.
                                    deliveredAt={orderHandoverAt(order)}
                                    source={from ?? 'order_detail'}
                                />
                            </div>
                        </FormSection>
                    </div>

                    {/* Left column — customer + timeline + auxiliary panels */}
                    <div className="min-w-0 space-y-5 lg:col-start-1 lg:row-start-1">
                        <FormSection
                            icon={<Users />}
                            title={m.order_customer_info()}
                        >
                            <div className="space-y-1.5 text-sm">
                                <Row
                                    label={m.customer_name()}
                                    value={order.customer_name}
                                />
                                <Row
                                    label={m.customer_phone()}
                                    value={order.customer_phone}
                                />
                                {ordererLine(order) && (
                                    <Row
                                        label={m.order_orderer_label()}
                                        value={ordererLine(order)}
                                    />
                                )}
                                <Row
                                    label={m.order_shipping_address()}
                                    value={order.shipping_address}
                                />
                                {order.shipping_ward && (
                                    <Row
                                        label={m.customer_ward_label()}
                                        value={order.shipping_ward}
                                    />
                                )}
                                {order.shipping_province && (
                                    <Row
                                        label={m.customer_province_label()}
                                        value={order.shipping_province}
                                    />
                                )}
                                {order.shipping_carrier_name && (
                                    <Row
                                        label={m.shipping_carrier()}
                                        value={order.shipping_carrier_name}
                                    />
                                )}
                                <Row
                                    label={m.order_channel_label()}
                                    value={
                                        <OrderChannelBadge
                                            channel={order.source_channel}
                                        />
                                    }
                                />
                                {(order.collect_cod === true ||
                                    order.collect_cod === false) && (
                                    <Row
                                        label={m.order_form_collect_cod_label()}
                                        value={
                                            order.collect_cod
                                                ? m.common_yes()
                                                : m.common_no()
                                        }
                                    />
                                )}
                                {(order.shipping_fee_payer === 'customer' ||
                                    order.shipping_fee_payer === 'company') && (
                                    <Row
                                        label={m.order_form_collect_freight_label()}
                                        value={
                                            order.shipping_fee_payer ===
                                            'customer'
                                                ? m.order_freight_status_customer()
                                                : m.order_freight_status_company()
                                        }
                                    />
                                )}
                                <InternalOrderCodeRow
                                    orderId={order.id}
                                    createdById={order.created_by_id}
                                    internalOrderCode={
                                        order.internal_order_code
                                    }
                                />
                                {order.product_notes && (
                                    <Row
                                        label={m.order_product_notes_label()}
                                        value={order.product_notes}
                                    />
                                )}
                            </div>
                        </FormSection>
                        <OrderGiftCard gift={order.gift} />
                        <OrderDeliveryCameraCard order={order} />
                        <SalesReturnHistory
                            orderId={orderId}
                            enabled={
                                // Trả hết hàng thì đơn chuyển "Đã hoàn" — vẫn
                                // cần xem các phiếu trả.
                                order.status === 'delivered' ||
                                order.status === 'returned'
                            }
                        />
                        <OrderPackagingDeducted orderId={orderId} />
                        <FormSection
                            icon={<History />}
                            title={m.order_status_history()}
                        >
                            <OrderTimeline
                                logs={timeline}
                                isLoading={timelineLoading}
                            />
                        </FormSection>
                    </div>
                </div>
            )}

            {/* QR Payment dialog */}
            <PaymentQRDialog
                open={qrOpen}
                onOpenChange={setQrOpen}
                orderId={orderId}
            />

            {/* Payment dialog */}
            <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{m.order_record_payment()}</DialogTitle>
                    </DialogHeader>
                    {/* key: remount with the refetched remaining after an
                        OVERPAYMENT so the amount and its limit are not stale. */}
                    <PaymentForm
                        key={remaining}
                        defaultAmount={paymentDefault}
                        maxAmount={Math.max(remaining, 0)}
                        onValuesChange={(values) =>
                            paymentTracking.formChanged(values, paymentDefault)
                        }
                        onInvalid={paymentTracking.formInvalid}
                        onSubmit={async (data) => {
                            await paymentTracking.submitDialog(
                                data,
                                paymentDefault,
                                () => recordPayments.mutateAsync([data]),
                            )
                            setPayOpen(false)
                        }}
                        onCancel={() => setPayOpen(false)}
                        isPending={recordPayments.isPending}
                    />
                </DialogContent>
            </Dialog>
        </div>
    )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
    return (
        <div className="flex justify-between gap-4">
            <span className="text-muted-foreground shrink-0">{label}</span>
            <span className="text-right">{value}</span>
        </div>
    )
}
